/* Product and cart UI for sc-split-test only. Does not replace fetch or native checkout. */
(() => {
  'use strict';

  const core = window.SCShippingCore;
  const root = window.Shopify?.routes?.root || '/';

  const money = (amount, currency) =>
    new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency
    }).format(amount / 100);

  async function jsonRequest(path, data) {
    const response = await fetch(`${root}${path}`, {
      method: data ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        ...(data
          ? {
              'Content-Type': 'application/json'
            }
          : {})
      },
      ...(data
        ? {
            body: JSON.stringify(data)
          }
        : {})
    });

    const body = await response.json();

    if (
      !response.ok ||
      body.status ||
      body.errors
    ) {
      throw new Error(
        body.description ||
        (
          typeof body.errors === 'string'
            ? body.errors
            : ''
        ) ||
        'No se pudo actualizar el carrito. Revisa el reparto antes de continuar.'
      );
    }

    return body;
  }

  async function loadProduct(path) {
    const url = new URL(
      path,
      location.origin
    );

    if (url.origin !== location.origin) {
      throw new Error(
        'Dirección de producto no válida.'
      );
    }

    url.search = '';
    url.searchParams.set(
      'view',
      'sc-shipping-data'
    );

    url.searchParams.set(
      '_sc',
      String(Date.now())
    );

    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(
        'No se pudo consultar el inventario. Vuelve a intentarlo.'
      );
    }

    return response.json();
  }

  const engine = new core.Engine({
    request: jsonRequest,
    loadProduct,
    storage: window.sessionStorage
  });

  const element = (
    tag,
    text,
    className
  ) => {
    const node =
      document.createElement(tag);

    if (text != null) {
      node.textContent = text;
    }

    if (className) {
      node.className = className;
    }

    return node;
  };

  function updateCartCount(cart) {
    const icon =
      document.querySelector(
        '#cart-icon-bubble'
      );

    if (!icon) return;

    let bubble =
      icon.querySelector(
        '.cart-count-bubble'
      );

    if (!cart.item_count) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = element(
        'div',
        null,
        'cart-count-bubble'
      );

      icon.append(bubble);
    }

    bubble.replaceChildren();

    if (cart.item_count < 100) {
      const number = element(
        'span',
        String(cart.item_count)
      );

      number.setAttribute(
        'aria-hidden',
        'true'
      );

      bubble.append(number);
    }

    bubble.append(
      element(
        'span',
        `${cart.item_count} ${
          cart.item_count === 1
            ? 'artículo'
            : 'artículos'
        }`,
        'visually-hidden'
      )
    );
  }

  async function openCart(
    snapshot,
    trigger
  ) {
    const drawer =
      document.querySelector(
        'cart-drawer'
      );

    if (!drawer) {
      location.assign(`${root}cart`);
      return;
    }

    await customElements.whenDefined(
      'cart-drawer'
    );

    window.scSplitTestCart = true;

    drawer.open(trigger, snapshot);
  }

  class SplitProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;

      this.error =
        this.querySelector(
          '[data-sc-error]'
        );

      this.button =
        this.querySelector(
          '[data-sc-add]'
        );

      this.form =
        this.querySelector('form');

      this.retry =
        this.querySelector(
          '[data-sc-product-retry]'
        );

      this.retry.addEventListener(
        'click',
        async () => {
          if (this.busy) return;

          this.busy = true;
          this.retry.disabled = true;

          try {
            const snapshot =
              await engine.review();

            await openCart(
              snapshot,
              this.retry
            );
          } catch (error) {
            this.showError(error);
            this.busy = false;
            this.retry.disabled = false;
          } finally {
            this.busy = false;
            this.retry.disabled = false;
          }
        }
      );

      this.selects = [
        ...this.querySelectorAll(
          '[data-sc-option]'
        )
      ];

      try {
        this.config = JSON.parse(
          this.querySelector(
            '[data-sc-config]'
          ).textContent
        );

        if (!this.config.enabled) {
          throw new Error(
            'Este producto necesita la etiqueta sc-split-test para habilitar la prueba.'
          );
        }

        this.pairs =
          core.buildPairs(this.config);

        const visiblePairs =
          this.pairs.filter(
            (pair) =>
              !pair.stockOnly ||
              pair.express.inventory > 0
          );

        this.selects[0].replaceChildren(
          ...[
            ...new Set(
              visiblePairs.map(
                (pair) => pair.options[0]
              )
            )
          ].map(
            (value) =>
              new Option(value, value)
          )
        );

        const sizes = () => {
          const previous =
            this.selects[1].value;

          this.selects[1].replaceChildren(
            ...[
              ...new Set(
                visiblePairs
                  .filter(
                    (pair) =>
                      pair.options[0] ===
                      this.selects[0].value
                  )
                  .map(
                    (pair) =>
                      pair.options[1]
                  )
              )
            ].map(
              (value) =>
                new Option(value, value)
            )
          );

          if (
            [...this.selects[1].options]
              .some(
                (option) =>
                  option.value === previous
              )
          ) {
            this.selects[1].value =
              previous;
          }

          this.choose();
        };

        sizes();
        this.choose();

        this.selects[0].addEventListener(
          'change',
          sizes
        );

        this.selects[1].addEventListener(
          'change',
          () => this.choose()
        );

        this.form.addEventListener(
          'submit',
          (event) => this.submit(event)
        );

        if (engine.pending()) {
          this.showError(
            new Error(
              'Hay un cambio pendiente. Revisa el carrito antes de agregar más artículos.'
            )
          );
        }
      } catch (error) {
        this.showError(error);
      }
    }

    choose() {
      this.pair = this.pairs.find(
        (pair) =>
          pair.options.every(
            (value, index) =>
              value ===
              this.selects[index].value
          )
      );

      this.button.disabled =
        !this.pair;

      const quantity =
        this.querySelector(
          '[data-sc-quantity]'
        );

      if (this.pair?.stockOnly) {
        quantity.max = String(
          Math.max(
            0,
            this.pair.express.inventory
          )
        );
      } else {
        quantity.removeAttribute('max');
      }

      this.querySelector(
        '[data-sc-price]'
      ).textContent = this.pair
        ? money(
            this.pair.express.price,
            this.config.currency
          )
        : 'Combinación no disponible';

      this.error.hidden = true;
    }

    showError(error) {
      this.error.textContent =
        error.message;

      this.error.hidden = false;

      if (this.retry) {
        this.retry.hidden =
          !engine.pending();
      }
    }

    async submit(event) {
      event.preventDefault();

      if (!this.pair || this.busy) {
        return;
      }

      const quantity = Number(
        this.querySelector(
          '[data-sc-quantity]'
        ).value
      );

      if (
        !Number.isSafeInteger(quantity) ||
        quantity < 1
      ) {
        return this.showError(
          new Error(
            'Introduce una cantidad entera mayor que cero.'
          )
        );
      }

      this.busy = true;
      this.button.disabled = true;

      this.setAttribute(
        'aria-busy',
        'true'
      );

      try {
        const snapshot =
          await engine.add(
            this.config.url,
            this.pair.id,
            quantity
          );

        await openCart(
          snapshot,
          this.button
        );
      } catch (error) {
        this.showError(error);
        this.busy = false;
        this.button.disabled = false;

        this.removeAttribute(
          'aria-busy'
        );
      } finally {
        this.busy = false;
        this.button.disabled = false;

        this.removeAttribute(
          'aria-busy'
        );
      }
    }
  }

  class SplitCart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;

      this.error =
        this.querySelector(
          '[data-sc-error]'
        );

      this.checkout =
        this.querySelector(
          '[data-sc-checkout]'
        );

      this.retry =
        this.querySelector(
          '[data-sc-retry]'
        );

      this.retry.addEventListener(
        'click',
        () =>
          this.run(
            () => engine.review()
          )
      );

      this.checkout.addEventListener(
        'click',
        () =>
          this.run(
            () => engine.review(),
            true
          )
      );

      if (
        !this.hasAttribute(
          'data-sc-drawer'
        )
      ) {
        this.run(
          () => engine.review()
        );
      }
    }

    activate(snapshot) {
      this.hidden = false;

      const drawer =
        this.closest('cart-drawer');

      drawer?.classList.remove(
        'is-empty'
      );

      drawer
        ?.querySelector(
          '.drawer__inner'
        )
        ?.setAttribute(
          'data-sc-drawer-mode',
          'true'
        );

      if (this.busy) {
        this.refreshRequested = true;
        return;
      }

      return this.run(
        () =>
          snapshot
            ? Promise.resolve(snapshot)
            : engine.review()
      );
    }

    setBusy(busy) {
      this.busy = busy;

      this.setAttribute(
        'aria-busy',
        String(busy)
      );

      this.querySelectorAll(
        'input, button'
      ).forEach((control) => {
        control.disabled = busy;
      });
    }

    async run(
      operation,
      checkout = false
    ) {
      if (this.busy) return;

      const focusedControl =
        document.activeElement
          ?.getAttribute(
            'data-sc-focus'
          );

      this.setBusy(true);
      this.error.hidden = true;

      try {
        const snapshot =
          await operation();

        this.render(snapshot);

        if (
          checkout &&
          snapshot.cart.item_count > 0
        ) {
          this.setBusy(false);
          this.checkout.disabled = true;

          location.assign(
            `${root}checkout`
          );

          return;
        }

        this.setBusy(false);

        this.checkout.disabled =
          snapshot.cart.item_count === 0;

        this.retry.hidden = true;
      } catch (error) {
        try {
          this.render(
            await engine.snapshot()
          );
        } catch (_) {
          // Keep the error visible.
        }

        this.setBusy(false);
        this.checkout.disabled = true;

        this.error.textContent =
          `${error.message} ` +
          'No continúes al pago hasta completar la revisión.';

        this.error.hidden = false;
        this.retry.hidden = false;
      } finally {
        const drawer =
          this.closest('cart-drawer');

        if (
          drawer?.classList.contains(
            'active'
          ) &&
          typeof trapFocus === 'function'
        ) {
          const inner =
            drawer.querySelector(
              '.drawer__inner'
            );

          const restored =
            focusedControl &&
            [
              ...this.querySelectorAll(
                '[data-sc-focus]'
              )
            ].find(
              (node) =>
                node.getAttribute(
                  'data-sc-focus'
                ) === focusedControl &&
                !node.disabled
            );

          trapFocus(
            inner,
            restored ||
              drawer.querySelector(
                '.drawer__close'
              ) ||
              inner
          );
        }

        if (this.refreshRequested) {
          this.refreshRequested = false;

          this.run(
            () => engine.review()
          );
        }
      }
    }

    controls(
      article,
      total,
      apply,
      key
    ) {
      if (
        this.hasAttribute(
          'data-sc-drawer'
        )
      ) {
        const controls = element(
          'div',
          null,
          'sc-split__quantity-controls'
        );

        const quantity = element(
          'div',
          null,
          'quantity'
        );

        const input =
          element('input');

        input.className =
          'quantity__input';

        input.type = 'number';
        input.min = '0';
        input.step = '1';
        input.value = total;

        input.setAttribute(
          'aria-label',
          'Cantidad total de este producto'
        );

        const commit = (value) => {
          if (this.busy) return;

          if (
            !Number.isSafeInteger(
              value
            ) ||
            value < 0
          ) {
            input.setCustomValidity(
              'Introduce una cantidad entera igual o mayor que cero.'
            );

            input.reportValidity();
            return;
          }

          input.setCustomValidity('');

          this.run(
            () => apply(value)
          );
        };

        const minus = element(
          'button',
          '−',
          'quantity__button'
        );

        const plus = element(
          'button',
          '+',
          'quantity__button'
        );

        minus.type =
          plus.type = 'button';

        minus.setAttribute(
          'aria-label',
          'Reducir cantidad'
        );

        plus.setAttribute(
          'aria-label',
          'Aumentar cantidad'
        );

        minus.addEventListener(
          'click',
          () =>
            commit(
              Math.max(
                0,
                Number(input.value) - 1
              )
            )
        );

        plus.addEventListener(
          'click',
          () =>
            commit(
              Number(input.value) + 1
            )
        );

        input.addEventListener(
          'change',
          () =>
            commit(
              Number(input.value)
            )
        );

        input.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();

              commit(
                Number(input.value)
              );
            }
          }
        );

        quantity.append(
          minus,
          input,
          plus
        );

        const remove = element(
          'button',
          'Eliminar',
          'sc-split__remove'
        );

        remove.type = 'button';

        remove.addEventListener(
          'click',
          () => commit(0)
        );

        for (
          const [role, control] of
          Object.entries({
            minus,
            quantity: input,
            plus,
            remove
          })
        ) {
          control.setAttribute(
            'data-sc-focus',
            `${key}:${role}`
          );
        }

        controls.append(
          quantity,
          remove
        );

        article.append(controls);

        return;
      }

      const label = element(
        'label',
        'Cantidad total'
      );

      const input =
        element('input');

      input.type = 'number';
      input.min = '0';
      input.step = '1';
      input.value = total;

      label.append(input);
      article.append(label);

      const update = element(
        'button',
        'Actualizar',
        'button button--secondary'
      );

      update.type = 'button';

      update.addEventListener(
        'click',
        () => {
          const value =
            Number(input.value);

          if (
            !Number.isSafeInteger(
              value
            ) ||
            value < 0
          ) {
            input.setCustomValidity(
              'Introduce una cantidad entera igual o mayor que cero.'
            );

            input.reportValidity();
            return;
          }

          input.setCustomValidity('');

          this.run(
            () => apply(value)
          );
        }
      );

      const remove = element(
        'button',
        'Eliminar',
        'button button--secondary'
      );

      remove.type = 'button';

      remove.addEventListener(
        'click',
        () =>
          this.run(
            () => apply(0)
          )
      );

      article.append(
        update,
        remove
      );
    }

    line(
      title,
      line,
      url,
      total,
      currency
    ) {
      const article = element(
        'article',
        null,
        'sc-split__line'
      );

      if (
        !this.hasAttribute(
          'data-sc-drawer'
        )
      ) {
        article.append(
          element('h2', title)
        );

        return {
          article,
          details: article
        };
      }

      const image = element(
        'div',
        null,
        'sc-split__cart-image'
      );

      const source =
        line?.featured_image?.url ||
        line?.image;

      if (typeof source === 'string') {
        const imageUrl = new URL(
          source,
          location.origin
        );

        if (
          ['https:', 'http:'].includes(
            imageUrl.protocol
          )
        ) {
          const imageElement =
            element('img');

          imageElement.src =
            imageUrl.href;

          imageElement.alt =
            line.featured_image?.alt ||
            title;

          imageElement.loading =
            'lazy';

          imageElement.width = 90;
          imageElement.height = 110;

          image.append(imageElement);
        }
      }

      const details = element(
        'div',
        null,
        'sc-split__cart-details'
      );

      const heading =
        element('h2');

      const productUrl = new URL(
        url || root,
        location.origin
      );

      if (
        productUrl.origin ===
        location.origin
      ) {
        const link = element(
          'a',
          title,
          'cart-item__name h4 break'
        );

        link.href = productUrl.href;
        heading.append(link);
      } else {
        heading.textContent = title;
      }

      details.append(heading);

      const price = element(
        'p',
        money(total, currency),
        'sc-split__cart-price'
      );

      article.append(
        image,
        details,
        price
      );

      return {
        article,
        details
      };
    }

    render(snapshot) {
      const {
        cart,
        groups,
        ordinary
      } = snapshot;

      updateCartCount(cart);

      const holder =
        this.querySelector(
          '[data-sc-cart-lines]'
        );

      holder.replaceChildren();

      if (!cart.item_count) {
        holder.append(
          element(
            'p',
            'Tu carrito está vacío.'
          )
        );
      }

      for (const group of groups) {
        const amount = [
          ...group.lines,
          ...(group.fees || [])
        ].reduce(
          (sum, line) =>
            sum + line.final_line_price,
          0
        );

        const {
          article,
          details
        } = this.line(
          `${group.pair.product.title} — ` +
          `${group.pair.options.join(' / ')}`,
          group.lines[0],
          group.pair.product.url,
          amount,
          cart.currency
        );

        const personal =
          group.pair.personalization;

        if (personal) {
          details.append(
            element(
              'p',
              `${
                personal.included
                  ? 'Dorsal incluido'
                  : 'Con dorsal'
              } · ${personal.name} · ` +
              `${personal.number}`
            )
          );
        }

        if (group.pair.patches) {
          details.append(
            element(
              'p',
              'Con parches'
            )
          );
        }

        if (group.express) {
          const delivery =
            element('p');

          delivery.append(
            element(
              'strong',
              `${group.express} ${
                group.express === 1
                  ? 'unidad'
                  : 'unidades'
              } · ENVÍO EXPRESS`
            )
          );

          details.append(delivery);
        }

        if (group.standard) {
          const delivery =
            element('p');

          delivery.append(
            element(
              'strong',
              `${group.standard} ${
                group.standard === 1
                  ? 'unidad'
                  : 'unidades'
              } · ENVÍO ESTÁNDAR`
            )
          );

          details.append(delivery);
        }

        if (group.pair.fee) {
          const count =
            (group.fees || []).reduce(
              (sum, line) =>
                sum + line.quantity,
              0
            );

          details.append(
            element(
              'p',
              count === group.total
                ? `${count} ${
                    count === 1
                      ? 'servicio de dorsal incluido'
                      : 'servicios de dorsal incluidos'
                  } en el importe de esta línea.`
                : 'Falta completar el ajuste del cobro del dorsal. Revisa de nuevo antes de pagar.'
            )
          );
        }

        if (
          !this.hasAttribute(
            'data-sc-drawer'
          )
        ) {
          details.append(
            element(
              'p',
              money(
                amount,
                cart.currency
              )
            )
          );
        }

        this.controls(
          details,
          group.total,
          (total) =>
            engine.setTotal(
              group.pair.id,
              total
            ),
          group.pair.id
        );

        holder.append(article);
      }

      for (const line of ordinary) {
        const {
          article,
          details
        } = this.line(
          line.product_title,
          line,
          line.url,
          line.final_line_price,
          cart.currency
        );

        details.append(
          element(
            'p',
            line.variant_title || ''
          )
        );

        for (
          const [name, value] of
          Object.entries(
            line.properties || {}
          )
        ) {
          if (
            !name.startsWith('_') &&
            value
          ) {
            details.append(
              element(
                'p',
                `${name}: ${value}`
              )
            );
          }
        }

        if (
          !this.hasAttribute(
            'data-sc-drawer'
          )
        ) {
          details.append(
            element(
              'p',
              money(
                line.final_line_price,
                cart.currency
              )
            )
          );
        }

        this.controls(
          details,
          line.quantity,
          (quantity) =>
            engine.exclusive(
              async () => {
                if (engine.pending()) {
                  throw new Error(
                    'Completa primero la revisión pendiente.'
                  );
                }

                await jsonRequest(
                  'cart/change.js',
                  {
                    id: line.key,
                    quantity
                  }
                );

                return engine.reconcile();
              }
            ),
          line.key
        );

        holder.append(article);
      }

      const express = groups.reduce(
        (sum, group) =>
          sum + group.express,
        0
      );

      const standard = groups.reduce(
        (sum, group) =>
          sum + group.standard,
        0
      );

      this.querySelector(
        '[data-sc-cart-summary]'
      ).textContent = groups.length
        ? [
            express
              ? `${express} ENVÍO EXPRESS`
              : '',
            standard
              ? `${standard} ENVÍO ESTÁNDAR`
              : ''
          ]
            .filter(Boolean)
            .join(' · ')
        : '';

      this.querySelector(
        '[data-sc-total]'
      ).textContent =
        `${
          this.hasAttribute(
            'data-sc-drawer'
          )
            ? ''
            : 'Subtotal: '
        }${money(
          cart.total_price,
          cart.currency
        )}`;
    }
  }

  customElements.define(
    'sc-split-product',
    SplitProduct
  );

  customElements.define(
    'sc-split-cart',
    SplitCart
  );

  window.SCShippingPrototype = {
    engine,
    updateCartCount,
    openCart
  };
})();